import type { PropertyForestSnapshot } from '@/contexts/PropertyForestSnapshotContextState';

export type PropertyForestSnapshotOwner = symbol;

interface SnapshotLease {
  snapshot: PropertyForestSnapshot;
  version: number;
}

const snapshots = new Map<
  string,
  Map<PropertyForestSnapshotOwner, SnapshotLease>
>();
const listeners = new Map<string, Set<() => void>>();
let nextVersion = 0;

function notify(propertyId: string): void {
  listeners.get(propertyId)?.forEach((listener) => listener());
}

/** Publish route-owned forest data for governed map renderers. */
export function publishPropertyForestSnapshot(
  propertyId: string,
  owner: PropertyForestSnapshotOwner,
  snapshot: PropertyForestSnapshot,
): void {
  const leases = snapshots.get(propertyId) ?? new Map();
  leases.set(owner, { snapshot, version: ++nextVersion });
  snapshots.set(propertyId, leases);
  notify(propertyId);
}

export function clearPropertyForestSnapshot(
  propertyId: string,
  owner: PropertyForestSnapshotOwner,
): void {
  const leases = snapshots.get(propertyId);
  if (!leases?.delete(owner)) return;
  if (leases.size === 0) snapshots.delete(propertyId);
  notify(propertyId);
}

export function readPropertyForestSnapshot(
  propertyId: string,
): PropertyForestSnapshot | null {
  const leases = snapshots.get(propertyId);
  if (!leases) return null;
  let current: SnapshotLease | undefined;
  for (const lease of leases.values()) {
    if (!current || lease.version > current.version) current = lease;
  }
  return current?.snapshot ?? null;
}

export function subscribePropertyForestSnapshot(
  propertyId: string,
  listener: () => void,
): () => void {
  const propertyListeners = listeners.get(propertyId) ?? new Set<() => void>();
  propertyListeners.add(listener);
  listeners.set(propertyId, propertyListeners);
  return () => {
    propertyListeners.delete(listener);
    if (propertyListeners.size === 0) listeners.delete(propertyId);
  };
}
